// src/pages/ITCallSummaryDashboard.tsx — Call Summary by Department (Teams Calling)
//
// 4-tab dashboard: Overview | By Department | By Officer / Extension | Auto Attendants & Queues
// Data source: /api/it-call-summary/* (Microsoft Teams calling via dw_gold Fabric Warehouse)
// Replaces the legacy Cisco "Call Manager — Call Summary by Department" SSRS report.
//
// v1 = DEPARTMENT-LEVEL (Directorate deferred). Department comes from the Entra department on
// the fact (f.DepartmentName). Staff tabs exclude resource/service accounts by default; the
// resource accounts (IVRs / Auto Attendants / Call Queues) live in their own tab.
// Requires governed access to dashboards:it_call_summary.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Spinner, SpinnerSize, Dropdown, IDropdownOption,
  mergeStyleSets, keyframes, MessageBar, MessageBarType,
} from '@fluentui/react';
import {
  BarChart, Bar, AreaChart, Area, PieChart, Pie,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import {
  CaretLeft, ArrowsClockwise, Phone, PhoneIncoming, PhoneOutgoing,
  CheckCircle, XCircle, Info, CaretUp, CaretDown, CaretRight,
} from '@phosphor-icons/react';
import { accessService } from '../services/accessService';

// ── Palette ──────────────────────────────────────────────────────────────────
const INDIGO = '#4F46E5';
const GREEN  = '#2e873d';
const AMBER  = '#D97706';
const RED    = '#D32F2F';
const GREY   = '#747474';

const INDIGO_PALETTE = [
  '#4F46E5','#6366F1','#818CF8','#A5B4FC','#4338CA',
  '#7C3AED','#0891B2','#0D9488','#C7D2FE','#3730A3',
];
const DIRECTION_COLOURS: Record<string, string> = {
  Inbound: '#2563EB', Outbound: '#4F46E5', Unknown: '#9CA3AF',
};

// ── Tabs ──────────────────────────────────────────────────────────────────────
type TabKey = 'overview' | 'department' | 'officer' | 'auto';
const TABS: { key: TabKey; label: string }[] = [
  { key: 'overview',   label: 'Overview'              },
  { key: 'department', label: 'By Department'         },
  { key: 'officer',    label: 'By Officer / Extension' },
  { key: 'auto',       label: 'Auto Attendants & Queues' },
];

// ── Types ─────────────────────────────────────────────────────────────────────
interface FilterOptions {
  departments: string[];
  directions:  string[];
  callSources: string[];
  personTypes: string[];
}

interface OverviewData {
  kpis: {
    totalCalls: number; inbound: number; outbound: number;
    answered: number; abandoned: number;
    answeredPct: number | null; abandonedPct: number | null;
    avgDurationMin: number | null;
  };
  byDepartment:  { department: string; count: number; inbound: number; outbound: number }[];
  byDay:         { dateKey: number; label: string; count: number; answered: number }[];
  topExtensions: { owner: string; extension: string; department: string; count: number; inbound: number; outbound: number }[];
  footnotes:     { staffUnattributed: number; resourceCalls: number };
  meta: { dateFrom: number; dateTo: number; generatedAt: string };
}

// SSRS-parity summary matrix (report spec §7): one row per directory extension,
// zero-activity rows included; department subtotals + percentages computed here.
interface SummaryRow {
  department: string; extension: string; extensionOwner: string; isResourceAccount: boolean;
  incTotal: number; incAnswered: number; incAbandoned: number; incDiverted: number;
  incIntTotal: number; incIntAnswered: number; incIntAbandoned: number;
  incExtTotal: number; incExtAnswered: number; incExtAbandoned: number;
  outTotal: number; outInternal: number; outExternal: number; outNotAnswered: number;
}
interface SummaryData {
  rows: SummaryRow[];
  meta: { dateFrom: number; dateTo: number; unattributedLegs: number; generatedAt: string };
}

interface OfficerRow {
  owner: string; extension: string; personType: string; jobTitle: string; department: string;
  inbound: number; outbound: number; answered: number; abandoned: number;
  total: number; avgDurationMin: number | null;
}
interface OfficerData { detail: OfficerRow[]; meta: { dateFrom: number; dateTo: number; capped: number; generatedAt: string } }

interface AutoRow {
  owner: string; extension: string;
  inbound: number; outbound: number; answered: number; abandoned: number;
  total: number; avgDurationMin: number | null;
}
interface AutoData {
  kpis: { totalCalls: number; inbound: number; outbound: number; answeredPct: number | null; abandonedPct: number | null };
  detail: AutoRow[];
  meta: { dateFrom: number; dateTo: number; capped: number; generatedAt: string };
}

// ── Filter option sets ──────────────────────────────────────────────────────────
const DATE_RANGE_OPTIONS: IDropdownOption[] = [
  { key: '14d',  text: 'Last 14 days'   },
  { key: '30d',  text: 'Last 30 days'   },
  { key: '90d',  text: 'Last 3 months'  },
  { key: '180d', text: 'Last 6 months'  },
  { key: 'all',  text: 'All time'       },
];
const OUTCOME_OPTIONS = ['Answered', 'Abandoned'];

function buildDateKeys(preset: string): { from: number; to: number } {
  const today = new Date();
  const from  = new Date(today);
  const days: Record<string, number> = { '14d': 14, '30d': 30, '90d': 90, '180d': 180 };
  if (preset in days) {
    from.setDate(today.getDate() - days[preset]);
  } else {
    from.setFullYear(2026, 6, 1);   // all time — Teams calling has no history before 27 Jul 2026
  }
  const fmt = (d: Date) => parseInt(d.toISOString().slice(0, 10).replace(/-/g, ''), 10);
  return { from: fmt(from), to: fmt(today) };
}

function answeredColor(pct: number | null): string {
  if (pct === null) return GREY;
  if (pct >= 90) return GREEN;
  if (pct >= 75) return AMBER;
  return RED;
}
function answeredLabel(pct: number | null): string {
  if (pct === null) return '—';
  if (pct >= 90) return 'Healthy';
  if (pct >= 75) return 'Watch';
  return 'Low';
}

function fmtNum(n: number): string { return n.toLocaleString('en-AU'); }
function fmtMin(n: number | null): string { return n == null ? '—' : `${n.toFixed(1)}m`; }
function trunc(s: string, max = 26): string {
  return s && s.length > max ? s.slice(0, max) + '…' : (s || '');
}

const spinAnim = keyframes({ from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } });

// ── Styles (mirror CRM chrome) ──────────────────────────────────────────────────
const s = mergeStyleSets({
  page: {
    padding: 32, minHeight: 'calc(100vh - 52px)',
    background: '#F5F5F5', fontFamily: "'Segoe UI', system-ui, sans-serif",
    '@media (max-width:768px)': { padding: 16 },
  } as any,
  container: { maxWidth: 1280, margin: '0 auto' },
  headerRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 20, flexWrap: 'wrap' as const, gap: 8,
  },
  backBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    color: INDIGO, fontWeight: 600, fontSize: 13, cursor: 'pointer',
    background: 'none', border: 'none', padding: 0, marginBottom: 4,
  },
  pageTitle: {
    fontSize: 26, fontWeight: 800, color: '#1A1A1A',
    letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: 10,
  },
  pageSub: { fontSize: 13, color: '#747474', marginTop: 2 },
  refreshBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '7px 14px', background: '#fff', border: '1px solid #E1E1E1',
    borderRadius: 6, fontSize: 13, fontWeight: 600, color: '#484848',
    cursor: 'pointer', transition: 'all 0.15s',
    selectors: { ':hover': { borderColor: INDIGO, color: INDIGO } },
  } as any,
  filterGroup: { display: 'flex', flexDirection: 'column' as const, gap: 3 },
  filterLabel: {
    fontSize: 10, fontWeight: 700, color: '#9CA3AF',
    textTransform: 'uppercase' as const, letterSpacing: '0.07em',
  },
  clearBtn: {
    padding: '7px 14px', background: 'none', border: '1px solid #E1E1E1',
    borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#747474',
    cursor: 'pointer', alignSelf: 'flex-end',
    selectors: { ':hover': { borderColor: RED, color: RED } },
  } as any,
  tabBar: { display: 'flex', borderBottom: '2px solid #E1E1E1', marginTop: 16, marginBottom: 24, gap: 0, flexWrap: 'wrap' as const },
  tabBtn: {
    padding: '10px 20px', fontSize: 14, fontWeight: 600,
    background: 'none', border: 'none', cursor: 'pointer', transition: 'all 0.15s',
    borderBottom: '2px solid transparent', marginBottom: -2,
  },
  kpiRow: {
    display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0,1fr))', gap: 14, marginBottom: 24,
    '@media (max-width: 1080px)': { gridTemplateColumns: 'repeat(3, 1fr)' },
    '@media (max-width: 620px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
  } as any,
  kpiCard: {
    background: '#fff', border: '1px solid #E1E1E1', borderRadius: 10,
    padding: '16px 18px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  },
  kpiLabel: {
    fontSize: 10, fontWeight: 700, color: '#9CA3AF',
    textTransform: 'uppercase' as const, letterSpacing: '0.07em', marginBottom: 6,
  },
  kpiValue: { fontSize: 30, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: '1', fontVariantNumeric: 'tabular-nums' },
  kpiSub: { fontSize: 11, color: '#9CA3AF', marginTop: 4 },
  twoCol: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16,
    '@media (max-width: 860px)': { gridTemplateColumns: '1fr' },
  } as any,
  sectionCard: {
    background: '#fff', border: '1px solid #E1E1E1', borderRadius: 10,
    padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 16,
  },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: '#1A1A1A', marginBottom: 2 },
  sectionSub:   { fontSize: 12, color: '#9CA3AF', marginBottom: 14 },
  dataTable: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 12 },
  th: {
    textAlign: 'left' as const, padding: '8px 10px', fontWeight: 700,
    fontSize: 11, color: '#747474', background: '#F9FAFB',
    borderBottom: '1px solid #E1E1E1', cursor: 'pointer',
    userSelect: 'none' as const, whiteSpace: 'nowrap' as const,
    selectors: { ':hover': { background: '#F3F4F6' } },
  } as any,
  td: { padding: '7px 10px', borderBottom: '1px solid #F3F4F6', color: '#232323', verticalAlign: 'top' as const },
  centeredMsg: {
    display: 'flex', flexDirection: 'column' as const, alignItems: 'center',
    justifyContent: 'center', minHeight: 240, gap: 12,
    color: '#747474', fontSize: 14, textAlign: 'center' as const,
  },
  accessBox: {
    background: '#fff', border: '1px solid #E1E1E1', borderRadius: 10,
    padding: 56, textAlign: 'center' as const, color: '#747474',
  },
  accessTitle: { fontWeight: 700, fontSize: 18, color: '#1A1A1A', marginBottom: 8 },
});

const tooltipStyle = { backgroundColor: '#fff', border: '1px solid #E1E1E1', borderRadius: 6, color: '#232323', fontSize: 12 };

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ ...tooltipStyle, padding: '8px 12px' }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {payload.map((pl: any, i: number) => (
        <div key={i} style={{ color: pl.color || pl.fill || INDIGO }}>{pl.name}: {fmtNum(pl.value)}</div>
      ))}
    </div>
  );
}

// ── Generic sortable table ──────────────────────────────────────────────────────
interface Col<T> { key: keyof T; label: string; num?: boolean; render?: (row: T) => React.ReactNode }
function SortableTable<T extends Record<string, any>>({ columns, rows, initialSort }: {
  columns: Col<T>[]; rows: T[]; initialSort: keyof T;
}) {
  const [sortKey, setSortKey] = useState<keyof T>(initialSort);
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const sorted = React.useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a[sortKey]; const bv = b[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') return dir === 'asc' ? av - bv : bv - av;
      const as = String(av ?? ''); const bs = String(bv ?? '');
      return dir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
    });
    return copy;
  }, [rows, sortKey, dir]);
  const onSort = (kk: keyof T) => {
    if (kk === sortKey) setDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(kk); setDir('desc'); }
  };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className={s.dataTable}>
        <thead>
          <tr>
            {columns.map(c => (
              <th key={String(c.key)} className={s.th} style={c.num ? { textAlign: 'right' } : {}} onClick={() => onSort(c.key)}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, justifyContent: c.num ? 'flex-end' : 'flex-start' }}>
                  {c.label}
                  {sortKey === c.key && (dir === 'asc' ? <CaretUp size={9} weight="bold" /> : <CaretDown size={9} weight="bold" />)}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, i) => (
            <tr key={i}>
              {columns.map(c => (
                <td key={String(c.key)} className={s.td} style={c.num ? { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } : {}}>
                  {c.render ? c.render(row) : String(row[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── SSRS-parity summary matrix (report spec §7) ─────────────────────────────────
// Department group rows (rolled-up totals, click to expand) → extension detail
// rows ("<Extension> <Owners>"), every directory extension shown incl. zero-activity.
// Two-row header band mirrors the legacy SSRS grouping; percentages are computed
// here from the counts (never averaged).
const VOICEMAIL_NOTE =
  'Answered currently includes calls picked up by voicemail; a voicemail split is planned.';

function pctCell(total: number, answered: number): string {
  if (total <= 0) return '–';
  return (((total - answered) / total) * 100).toFixed(1) + '%';
}

interface MatrixTotals {
  incTotal: number; incAnswered: number; incAbandoned: number; incDiverted: number;
  incIntTotal: number; incIntAnswered: number; incIntAbandoned: number;
  incExtTotal: number; incExtAnswered: number; incExtAbandoned: number;
  outTotal: number; outInternal: number; outExternal: number; outNotAnswered: number;
}
const MATRIX_NUM_KEYS: (keyof MatrixTotals)[] = [
  'incTotal', 'incAnswered', 'incAbandoned', 'incDiverted',
  'incIntTotal', 'incIntAnswered', 'incIntAbandoned',
  'incExtTotal', 'incExtAnswered', 'incExtAbandoned',
  'outTotal', 'outInternal', 'outExternal', 'outNotAnswered',
];

function sumMatrix(rows: SummaryRow[]): MatrixTotals {
  const t = Object.fromEntries(MATRIX_NUM_KEYS.map(k => [k, 0])) as unknown as MatrixTotals;
  for (const r of rows) for (const k of MATRIX_NUM_KEYS) t[k] += r[k];
  return t;
}

// The 17 measure cells in SSRS column order (percentages interleaved).
function matrixCells(t: MatrixTotals): { v: string; em?: boolean; pct?: boolean }[] {
  return [
    { v: fmtNum(t.incTotal), em: true },
    { v: fmtNum(t.incAnswered) },
    { v: fmtNum(t.incAbandoned) },
    { v: fmtNum(t.incDiverted) },
    { v: pctCell(t.incTotal, t.incAnswered), pct: true },
    { v: fmtNum(t.incIntTotal) },
    { v: fmtNum(t.incIntAnswered) },
    { v: fmtNum(t.incIntAbandoned) },
    { v: pctCell(t.incIntTotal, t.incIntAnswered), pct: true },
    { v: fmtNum(t.incExtTotal) },
    { v: fmtNum(t.incExtAnswered) },
    { v: fmtNum(t.incExtAbandoned) },
    { v: pctCell(t.incExtTotal, t.incExtAnswered), pct: true },
    { v: fmtNum(t.outTotal), em: true },
    { v: fmtNum(t.outInternal) },
    { v: fmtNum(t.outExternal) },
    { v: fmtNum(t.outNotAnswered) },
  ];
}

const mx = {
  groupTh: {
    padding: '6px 8px', fontSize: 10.5, fontWeight: 800, letterSpacing: '0.04em',
    textAlign: 'center' as const, borderBottom: '1px solid #E1E1E1', borderLeft: '2px solid #E1E1E1',
    background: '#F9FAFB', whiteSpace: 'nowrap' as const,
  },
  th: {
    padding: '6px 7px', fontSize: 10, fontWeight: 700, color: '#747474', background: '#F9FAFB',
    borderBottom: '1px solid #E1E1E1', textAlign: 'right' as const, verticalAlign: 'bottom' as const,
    minWidth: 62, lineHeight: 1.25,
  },
  td: {
    padding: '5px 7px', borderBottom: '1px solid #F3F4F6', fontSize: 11.5,
    textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' as const, whiteSpace: 'nowrap' as const,
  },
};

function AnsweredInfo() {
  return (
    <span title={VOICEMAIL_NOTE} style={{ cursor: 'help', color: AMBER, fontWeight: 700 }}> ⓘ</span>
  );
}

function SummaryMatrix({ data }: { data: SummaryData }) {
  const [open, setOpen] = useState<Set<string>>(new Set());

  const groups = React.useMemo(() => {
    const map = new Map<string, SummaryRow[]>();
    for (const r of data.rows) {
      if (!map.has(r.department)) map.set(r.department, []);
      map.get(r.department)!.push(r);
    }
    return [...map.entries()].map(([dept, rows]) => ({ dept, rows, totals: sumMatrix(rows) }));
  }, [data.rows]);

  const allOpen = groups.length > 0 && open.size >= groups.length;
  const toggle = (d: string) =>
    setOpen(prev => {
      const next = new Set(prev);
      if (next.has(d)) next.delete(d); else next.add(d);
      return next;
    });

  const grand = React.useMemo(() => sumMatrix(data.rows), [data.rows]);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button className={s.clearBtn} style={{ alignSelf: 'auto' }}
          onClick={() => setOpen(allOpen ? new Set() : new Set(groups.map(g => g.dept)))}>
          {allOpen ? 'Collapse all departments' : 'Expand all departments'}
        </button>
      </div>
      <div style={{ overflowX: 'auto', maxHeight: 560, overflowY: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 1380 }}>
          <thead>
            <tr>
              <th style={{ ...mx.groupTh, textAlign: 'left', borderLeft: 'none', minWidth: 250 }} rowSpan={2}>
                Department / Extension
              </th>
              <th style={{ ...mx.groupTh, color: GREEN }} colSpan={5}>INCOMING CALLS</th>
              <th style={{ ...mx.groupTh, color: '#1A1A1A' }} colSpan={4}>Incoming Calls from Internal Extensions</th>
              <th style={{ ...mx.groupTh, color: '#1A1A1A' }} colSpan={4}>Incoming Calls from External Callers</th>
              <th style={{ ...mx.groupTh, color: GREEN }} colSpan={4}>OUTGOING CALLS</th>
            </tr>
            <tr>
              <th style={{ ...mx.th, borderLeft: '2px solid #E1E1E1', color: GREEN, fontWeight: 800 }}>Total Incoming</th>
              <th style={mx.th}>Total Answered<AnsweredInfo /></th>
              <th style={mx.th}>Total Abandoned</th>
              <th style={mx.th}>Total Diverted to Customer Service</th>
              <th style={mx.th}>% Incoming Abandoned/Diverted</th>
              <th style={{ ...mx.th, borderLeft: '2px solid #E1E1E1' }}>
                Incoming Internal
                <div style={{ fontSize: 9, fontStyle: 'italic', color: GREEN, fontWeight: 600 }}>(Incl. Diverted)</div>
              </th>
              <th style={mx.th}>Internal Answered<AnsweredInfo /></th>
              <th style={mx.th}>Internal Abandoned</th>
              <th style={mx.th}>% Internal Abandoned/Diverted</th>
              <th style={{ ...mx.th, borderLeft: '2px solid #E1E1E1' }}>
                Incoming External
                <div style={{ fontSize: 9, fontStyle: 'italic', color: GREEN, fontWeight: 600 }}>(Incl. Diverted)</div>
              </th>
              <th style={mx.th}>External Answered<AnsweredInfo /></th>
              <th style={mx.th}>External Abandoned</th>
              <th style={mx.th}>% External Abandoned/Diverted</th>
              <th style={{ ...mx.th, borderLeft: '2px solid #E1E1E1', color: GREEN, fontWeight: 800 }}>Total Outgoing</th>
              <th style={mx.th}>Outgoing Internal</th>
              <th style={mx.th}>Outgoing External</th>
              <th style={mx.th}>Outgoing Not Answered</th>
            </tr>
          </thead>
          <tbody>
            {groups.map(g => (
              <React.Fragment key={g.dept}>
                <tr style={{ cursor: 'pointer', background: '#EEF2FF' }} onClick={() => toggle(g.dept)}>
                  <td style={{ ...mx.td, textAlign: 'left', fontWeight: 700, whiteSpace: 'normal' }}>
                    {open.has(g.dept) ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}{' '}
                    {g.dept}
                    <span style={{ fontWeight: 400, color: '#6B7280' }}> · {g.rows.length} ext</span>
                  </td>
                  {matrixCells(g.totals).map((c, i) => (
                    <td key={i} style={{ ...mx.td, fontWeight: c.em ? 800 : 700, color: c.em ? GREEN : '#232323' }}>{c.v}</td>
                  ))}
                </tr>
                {open.has(g.dept) && g.rows.map(r => {
                  const idle = r.incTotal + r.outTotal === 0;
                  return (
                    <tr key={r.extension}>
                      <td style={{
                        ...mx.td, textAlign: 'left', paddingLeft: 26, whiteSpace: 'normal',
                        color: idle ? '#9CA3AF' : '#232323',
                      }}>
                        {r.extensionOwner}
                        {r.isResourceAccount && (
                          <span style={{
                            marginLeft: 6, fontSize: 9, fontWeight: 700, padding: '1px 6px',
                            borderRadius: 8, background: '#EEF2FF', color: '#4338CA', whiteSpace: 'nowrap',
                          }}>SHARED NUMBER</span>
                        )}
                      </td>
                      {matrixCells(r).map((c, i) => (
                        <td key={i} style={{
                          ...mx.td,
                          color: idle ? '#C4C4C4' : c.em ? '#1A1A1A' : '#232323',
                          fontWeight: c.em ? 700 : 400,
                        }}>{c.v}</td>
                      ))}
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: '#F9FAFB', borderTop: '2px solid #E1E1E1' }}>
              <td style={{ ...mx.td, textAlign: 'left', fontWeight: 800 }}>All departments</td>
              {matrixCells(grand).map((c, i) => (
                <td key={i} style={{ ...mx.td, fontWeight: 800, color: c.em ? GREEN : '#1A1A1A' }}>{c.v}</td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ITCallSummaryDashboard() {
  const nav = useNavigate();

  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('overview');

  // Filter state — multi-select arrays (empty = All; empty PersonType = all staff)
  const [dateRange,  setDateRange]  = useState('90d');
  const [department, setDepartment] = useState<string[]>([]);
  const [direction,  setDirection]  = useState<string[]>([]);
  const [outcome,    setOutcome]    = useState<string[]>([]);
  const [personType, setPersonType] = useState<string[]>([]);
  const [callSource, setCallSource] = useState<string[]>([]);

  const [filterOpts, setFilterOpts] = useState<FilterOptions | null>(null);
  const [filterOptsLoading, setFilterOptsLoading] = useState(false);

  const [overviewData, setOverviewData] = useState<OverviewData | null>(null);
  const [summaryData,  setSummaryData]  = useState<SummaryData | null>(null);
  const [officerData,  setOfficerData]  = useState<OfficerData | null>(null);
  const [autoData,     setAutoData]     = useState<AutoData | null>(null);

  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  // ── Access check ────────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await accessService.get<{ role?: string; allowedDashboards?: string[] }>('/me');
        if (!alive) return;
        const d = res.data;
        setHasAccess(d.role === 'admin'
          ? true
          : Array.isArray(d.allowedDashboards) && d.allowedDashboards.includes('it_call_summary'));
      } catch { setHasAccess(false); }
    })();
    return () => { alive = false; };
  }, []);

  // ── Load filter options once ─────────────────────────────────────────────────
  useEffect(() => {
    if (!hasAccess) return;
    setFilterOptsLoading(true);
    accessService.get<FilterOptions>('/it-call-summary/filter-options')
      .then(r => setFilterOpts(r.data))
      .catch(e => console.warn('[IT-Call] filter-options failed:', e.message))
      .finally(() => setFilterOptsLoading(false));
  }, [hasAccess]);

  // ── Common filter params ─────────────────────────────────────────────────────
  const filterParams = useCallback(() => {
    const { from, to } = buildDateKeys(dateRange);
    return {
      dateFrom: from, dateTo: to,
      ...(department.length ? { department: department.join(',') } : {}),
      ...(direction.length  ? { direction:  direction.join(',') }  : {}),
      ...(outcome.length    ? { outcome:    outcome.join(',') }    : {}),
      ...(personType.length ? { personType: personType.join(',') } : {}),
      ...(callSource.length ? { callSource: callSource.join(',') } : {}),
    };
  }, [dateRange, department, direction, outcome, personType, callSource]);

  useEffect(() => {
    setOverviewData(null); setSummaryData(null); setOfficerData(null); setAutoData(null);
  }, [dateRange, department, direction, outcome, personType, callSource]);

  // ── Fetch helper ─────────────────────────────────────────────────────────────
  const fetchCurrentTab = useCallback(async (tab: TabKey) => {
    if (!hasAccess) return;
    setLoading(true); setError(null);
    try {
      const params = filterParams();
      if (tab === 'overview') {
        const r = await accessService.get<OverviewData>('/it-call-summary/overview', { params });
        setOverviewData(r.data);
      } else if (tab === 'department') {
        const r = await accessService.get<SummaryData>('/it-call-summary/summary', { params });
        setSummaryData(r.data);
      } else if (tab === 'officer') {
        const r = await accessService.get<OfficerData>('/it-call-summary/by-officer', { params });
        setOfficerData(r.data);
      } else if (tab === 'auto') {
        const r = await accessService.get<AutoData>('/it-call-summary/auto-attendants', { params });
        setAutoData(r.data);
      }
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [hasAccess, filterParams]);

  useEffect(() => {
    if (!hasAccess) return;
    fetchCurrentTab(activeTab);
  }, [activeTab, fetchCurrentTab, hasAccess]);

  const clearFilters = () => {
    setDateRange('90d');
    setDepartment([]); setDirection([]); setOutcome([]); setPersonType([]); setCallSource([]);
  };
  const hasActiveFilter =
    department.length > 0 || direction.length > 0 || outcome.length > 0 ||
    personType.length > 0 || callSource.length > 0 || dateRange !== '90d';

  const refreshedAt = (() => {
    const d = overviewData?.meta.generatedAt || summaryData?.meta.generatedAt
      || officerData?.meta.generatedAt || autoData?.meta.generatedAt;
    return d ? new Date(d).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) : null;
  })();

  const multiToggle = (setter: React.Dispatch<React.SetStateAction<string[]>>) =>
    (_: any, o?: IDropdownOption) => {
      if (!o) return;
      const v = String(o.key);
      setter(prev => o.selected ? [...prev, v] : prev.filter(x => x !== v));
    };

  // ── Access gate ───────────────────────────────────────────────────────────────
  if (hasAccess === null) {
    return (
      <div className={s.page}>
        <div className={s.container} style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
          <Spinner size={SpinnerSize.large} label="Checking access…" />
        </div>
      </div>
    );
  }
  if (hasAccess === false) {
    return (
      <div className={s.page}>
        <div className={s.container}>
          <button className={s.backBtn} onClick={() => nav('/dashboards')}>
            <CaretLeft size={12} weight="bold" /> Dashboards
          </button>
          <div className={s.accessBox}>
            <div className={s.accessTitle}>Access Required</div>
            <div style={{ fontSize: 14, color: '#747474', maxWidth: 460, margin: '0 auto' }}>
              The Call Summary by Department dashboard requires governed access.
              Submit an access request to your administrator to get started.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const k = overviewData?.kpis;
  const foot = overviewData?.footnotes;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className={s.page}>
      <div className={s.container}>

        <button className={s.backBtn} onClick={() => nav('/dashboards')}>
          <CaretLeft size={12} weight="bold" /> Dashboards
        </button>

        {/* Header */}
        <div className={s.headerRow}>
          <div>
            <div className={s.pageTitle}>
              <span style={{
                width: 36, height: 36, borderRadius: 8, background: INDIGO,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Phone size={18} color="#fff" weight="bold" />
              </span>
              Call Summary by Department
            </div>
            <div className={s.pageSub}>
              Teams Calling · {refreshedAt ? `Updated ${refreshedAt}` : 'Loading…'}
            </div>
          </div>
          <button
            className={s.refreshBtn}
            onClick={() => {
              if (activeTab === 'overview')   setOverviewData(null);
              if (activeTab === 'department') setSummaryData(null);
              if (activeTab === 'officer')    setOfficerData(null);
              if (activeTab === 'auto')       setAutoData(null);
              fetchCurrentTab(activeTab);
            }}
            disabled={loading}
          >
            <ArrowsClockwise size={14} weight="bold" style={loading ? { animation: `${spinAnim} 1s linear infinite` } : {}} />
            Refresh
          </button>
        </div>

        {error && (
          <MessageBar messageBarType={MessageBarType.error} styles={{ root: { marginBottom: 12, borderRadius: 8 } }}>
            {error}
          </MessageBar>
        )}

        {/* Filter bar */}
        <div style={{
          background: '#fff', border: '1px solid #E1E1E1', borderRadius: 8,
          padding: '14px 20px', marginBottom: 0, display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10,
        }}>
          <div className={s.filterGroup}>
            <span className={s.filterLabel}>Period</span>
            <Dropdown options={DATE_RANGE_OPTIONS} selectedKey={dateRange}
              onChange={(_, o) => o && setDateRange(String(o.key))}
              styles={{ root: { minWidth: 148 }, dropdown: { fontSize: 13 } }} />
          </div>
          <div style={{ width: 1, background: '#E1E1E1', alignSelf: 'stretch', margin: '0 6px', flexShrink: 0 }} />
          <div className={s.filterGroup}>
            <span className={s.filterLabel}>Department</span>
            <Dropdown multiSelect placeholder="All departments"
              options={(filterOpts?.departments ?? []).map(d => ({ key: d, text: d }))}
              selectedKeys={department} onChange={multiToggle(setDepartment)}
              disabled={filterOptsLoading || !filterOpts?.departments.length}
              styles={{ root: { minWidth: 200 }, dropdown: { fontSize: 13 }, callout: { minWidth: 340 } }} />
          </div>
          {/* Direction / Outcome / Person Type don't apply to the summary matrix —
              its columns already split direction and outcome, and shared numbers
              are shown inline — so they're disabled on that tab. */}
          <div className={s.filterGroup}>
            <span className={s.filterLabel} style={{ color: activeTab === 'department' ? '#D1D5DB' : undefined }}>Direction</span>
            <Dropdown multiSelect placeholder="All directions"
              options={(filterOpts?.directions ?? ['Inbound', 'Outbound']).map(d => ({ key: d, text: d }))}
              selectedKeys={direction} onChange={multiToggle(setDirection)}
              disabled={filterOptsLoading || activeTab === 'department'}
              styles={{ root: { minWidth: 150 }, dropdown: { fontSize: 13 } }} />
          </div>
          <div className={s.filterGroup}>
            <span className={s.filterLabel} style={{ color: activeTab === 'department' ? '#D1D5DB' : undefined }}>Outcome</span>
            <Dropdown multiSelect placeholder="All outcomes"
              options={OUTCOME_OPTIONS.map(o => ({ key: o, text: o }))}
              selectedKeys={outcome} onChange={multiToggle(setOutcome)}
              disabled={activeTab === 'department'}
              styles={{ root: { minWidth: 150 }, dropdown: { fontSize: 13 } }} />
          </div>
          <div className={s.filterGroup}>
            <span className={s.filterLabel} style={{ color: activeTab === 'department' ? '#D1D5DB' : undefined }}>Person Type</span>
            <Dropdown multiSelect placeholder="Staff (default)"
              options={(filterOpts?.personTypes ?? []).map(pt => ({ key: pt, text: pt }))}
              selectedKeys={personType} onChange={multiToggle(setPersonType)}
              disabled={filterOptsLoading || !filterOpts?.personTypes.length || activeTab === 'department'}
              styles={{ root: { minWidth: 165 }, dropdown: { fontSize: 13 }, callout: { minWidth: 240 } }} />
          </div>
          <div className={s.filterGroup}>
            <span className={s.filterLabel}>Call Source</span>
            <Dropdown multiSelect placeholder="All sources"
              options={(filterOpts?.callSources ?? []).map(c => ({ key: c, text: c === 'direct_routing' ? 'Direct Routing' : c === 'pstn' ? 'PSTN' : c }))}
              selectedKeys={callSource} onChange={multiToggle(setCallSource)}
              disabled={filterOptsLoading || !filterOpts?.callSources.length}
              styles={{ root: { minWidth: 160 }, dropdown: { fontSize: 13 } }} />
          </div>
          {hasActiveFilter && (
            <button className={s.clearBtn} onClick={clearFilters} style={{ alignSelf: 'flex-end' }}>
              Clear filters
            </button>
          )}
        </div>

        {/* Tab bar */}
        <div className={s.tabBar}>
          {TABS.map(tab => (
            <button key={tab.key} className={s.tabBtn} onClick={() => setActiveTab(tab.key)}
              style={{
                color: activeTab === tab.key ? INDIGO : '#747474',
                borderBottom: activeTab === tab.key ? `2px solid ${INDIGO}` : '2px solid transparent',
              }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Methodology / exclusions strip */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 7, marginTop: 12, marginBottom: 12,
          fontSize: 12, color: '#6B7280', background: '#F9FAFB', border: '1px solid #E5E7EB',
          borderRadius: 6, padding: '8px 12px', lineHeight: 1.5,
        }}>
          <Info size={14} color="#6B7280" style={{ marginTop: 2, flexShrink: 0 }} />
          <span>
            <strong>What this shows:</strong> phone calls staff made or received on Microsoft Teams.
            The <strong>By Department</strong> summary covers internal (staff-to-staff) and external calls;
            the Overview and By Officer tabs cover external calls only. Shared numbers (main switchboards,
            auto-attendants and call queues) appear within their department in the summary and on the{' '}
            <strong>Auto Attendants &amp; Queues</strong> tab. Answered figures marked ⓘ currently include
            calls picked up by voicemail — a voicemail split is planned. “Diverted to Customer Service”
            shows 0 until that measure is available. Microsoft Teams only keeps recent call records, so this
            warehouse retains the full history and is the long-term record. Updated daily around 5 AM.
            {foot && (foot.staffUnattributed > 0 || foot.resourceCalls > 0) && (
              <>
                {' '}For the current view, <strong>{fmtNum(foot.staffUnattributed)}</strong> staff call
                {foot.staffUnattributed === 1 ? '' : 's'} {foot.staffUnattributed === 1 ? "isn't" : "aren't"}{' '}
                linked to a department yet (so {foot.staffUnattributed === 1 ? "it's" : "they're"} not in the
                department breakdown), and <strong>{fmtNum(foot.resourceCalls)}</strong> call
                {foot.resourceCalls === 1 ? '' : 's'} to shared numbers {foot.resourceCalls === 1 ? 'is' : 'are'}{' '}
                on the “Auto Attendants &amp; Queues” tab.
              </>
            )}
          </span>
        </div>

        {loading && (
          <div className={s.centeredMsg}>
            <Spinner size={SpinnerSize.large} />
            <span>Loading {TABS.find(t => t.key === activeTab)?.label}…</span>
          </div>
        )}

        {/* ══════════════ Tab 1: OVERVIEW ══════════════ */}
        {activeTab === 'overview' && !loading && overviewData && k && (() => {
          const { byDepartment, byDay, topExtensions } = overviewData;
          const ansClr = answeredColor(k.answeredPct);
          const directionData = [
            { name: 'Inbound', value: k.inbound },
            { name: 'Outbound', value: k.outbound },
          ];
          const outcomeData = [
            { name: 'Answered', value: k.answered },
            { name: 'Abandoned', value: k.abandoned },
          ];
          return (
            <>
              {/* KPIs */}
              <div className={s.kpiRow}>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>Total Calls</div>
                  <div className={s.kpiValue} style={{ color: INDIGO }}>{fmtNum(k.totalCalls)}</div>
                  <div className={s.kpiSub}>external calls by staff</div>
                </div>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>Inbound</div>
                  <div className={s.kpiValue} style={{ color: '#1A1A1A' }}>{fmtNum(k.inbound)}</div>
                  <div className={s.kpiSub} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <PhoneIncoming size={12} color={DIRECTION_COLOURS.Inbound} />
                    {k.totalCalls > 0 ? `${((k.inbound / k.totalCalls) * 100).toFixed(0)}% of calls` : '—'}
                  </div>
                </div>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>Outbound</div>
                  <div className={s.kpiValue} style={{ color: '#1A1A1A' }}>{fmtNum(k.outbound)}</div>
                  <div className={s.kpiSub} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <PhoneOutgoing size={12} color={DIRECTION_COLOURS.Outbound} />
                    {k.totalCalls > 0 ? `${((k.outbound / k.totalCalls) * 100).toFixed(0)}% of calls` : '—'}
                  </div>
                </div>
                <div className={s.kpiCard} style={{ borderLeft: `3px solid ${ansClr}` }}>
                  <div className={s.kpiLabel}>Answered</div>
                  {k.answeredPct !== null ? (
                    <>
                      <div className={s.kpiValue} style={{ color: ansClr }}>
                        {k.answeredPct.toFixed(1)}<span style={{ fontSize: 18, fontWeight: 600, marginLeft: 2 }}>%</span>
                      </div>
                      <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 4 }}>
                        {k.answeredPct >= 90 ? <CheckCircle size={12} color={GREEN} weight="fill" /> : <XCircle size={12} color={ansClr} weight="fill" />}
                        <span style={{ fontSize: 11, color: ansClr, fontWeight: 600 }}>{answeredLabel(k.answeredPct)}</span>
                      </div>
                    </>
                  ) : <div className={s.kpiValue} style={{ color: GREY }}>—</div>}
                </div>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>Abandoned</div>
                  <div className={s.kpiValue} style={{ color: k.abandoned > 0 ? RED : '#1A1A1A' }}>
                    {k.abandonedPct !== null ? `${k.abandonedPct.toFixed(1)}%` : '—'}
                  </div>
                  <div className={s.kpiSub}>{fmtNum(k.abandoned)} not connected</div>
                </div>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>Avg Duration</div>
                  <div className={s.kpiValue} style={{ color: '#1A1A1A' }}>{fmtMin(k.avgDurationMin)}</div>
                  <div className={s.kpiSub}>per call</div>
                </div>
              </div>

              {/* Direction + Outcome */}
              <div className={s.twoCol}>
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Inbound vs Outbound</div>
                  <div className={s.sectionSub}>Direction split · {fmtNum(k.totalCalls)} calls</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={directionData} layout="vertical" margin={{ top: 0, right: 40, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F3F4F6" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmtNum} />
                      <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12 }} />
                      <Tooltip content={<ChartTooltip />} />
                      <Bar dataKey="value" name="Calls" radius={[0, 4, 4, 0]} barSize={34}>
                        {directionData.map((e, i) => <Cell key={i} fill={DIRECTION_COLOURS[e.name] ?? INDIGO} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Answered vs Abandoned</div>
                  <div className={s.sectionSub}>Connected outcome (external-call proxy)</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={outcomeData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                        innerRadius={52} outerRadius={80} paddingAngle={2}>
                        <Cell fill={GREEN} /><Cell fill={RED} />
                      </Pie>
                      <Tooltip content={<ChartTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 20, fontSize: 12, marginTop: -6 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><i style={{ width: 9, height: 9, borderRadius: 2, background: GREEN, display: 'inline-block' }} />Answered {fmtNum(k.answered)}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><i style={{ width: 9, height: 9, borderRadius: 2, background: RED, display: 'inline-block' }} />Abandoned {fmtNum(k.abandoned)}</span>
                  </div>
                </div>
              </div>

              {/* Calls by Department — headline */}
              {byDepartment.length > 0 && (
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Calls by Department</div>
                  <div className={s.sectionSub}>Staff external calls by Entra department · top {byDepartment.length}</div>
                  <ResponsiveContainer width="100%" height={Math.max(160, byDepartment.length * 30)}>
                    <BarChart data={byDepartment} layout="vertical" margin={{ top: 0, right: 60, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F3F4F6" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmtNum} />
                      <YAxis type="category" dataKey="department" width={220} tick={{ fontSize: 11 }} tickFormatter={v => trunc(v, 32)} />
                      <Tooltip formatter={(v: any) => [fmtNum(v), 'Calls']} labelStyle={{ fontWeight: 600 }} contentStyle={tooltipStyle} />
                      <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={18}>
                        {byDepartment.map((_, i) => <Cell key={i} fill={INDIGO_PALETTE[i % INDIGO_PALETTE.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Calls over time */}
              <div className={s.sectionCard}>
                <div className={s.sectionTitle}>Calls Over Time</div>
                <div className={s.sectionSub}>Daily volume · external calls by staff</div>
                {byDay.length === 0
                  ? <div className={s.centeredMsg} style={{ minHeight: 160 }}>No calls in this range</div>
                  : (
                    <ResponsiveContainer width="100%" height={240}>
                      <AreaChart data={byDay} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                        <defs>
                          <linearGradient id="callGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={INDIGO} stopOpacity={0.18} />
                            <stop offset="95%" stopColor={INDIGO} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                        <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtNum} allowDecimals={false} />
                        <Tooltip content={<ChartTooltip />} />
                        <Area type="monotone" dataKey="count" name="Calls" stroke={INDIGO} strokeWidth={2} fill="url(#callGrad)" dot={byDay.length <= 40} />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
              </div>

              {/* Top extensions */}
              {topExtensions.length > 0 && (
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Top Officers / Extensions by Volume</div>
                  <div className={s.sectionSub}>Staff calls · top {topExtensions.length}</div>
                  <table className={s.dataTable}>
                    <thead>
                      <tr>
                        <th className={s.th}>Owner</th><th className={s.th}>Extension</th><th className={s.th}>Department</th>
                        <th className={s.th} style={{ textAlign: 'right' }}>Inbound</th>
                        <th className={s.th} style={{ textAlign: 'right' }}>Outbound</th>
                        <th className={s.th} style={{ textAlign: 'right' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topExtensions.map((e, i) => (
                        <tr key={i}>
                          <td className={s.td} style={{ fontWeight: 600 }}>{e.owner}</td>
                          <td className={s.td}>{e.extension}</td>
                          <td className={s.td}>{e.department}</td>
                          <td className={s.td} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(e.inbound)}</td>
                          <td className={s.td} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(e.outbound)}</td>
                          <td className={s.td} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtNum(e.count)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          );
        })()}

        {/* ══════════════ Tab 2: BY DEPARTMENT — SSRS-parity summary matrix ══════════════ */}
        {activeTab === 'department' && !loading && summaryData && (
          <div className={s.sectionCard}>
            <div className={s.sectionTitle}>Call Summary by Department</div>
            <div className={s.sectionSub}>
              Every directory extension is listed (including no-activity rows), grouped by department —
              click a department to see its extensions · shared numbers appear within their department ·
              Period, Department and Call Source filters apply; Direction, Outcome and Person Type don't
              (this table already splits them).
              {summaryData.meta.unattributedLegs > 0 && (
                <> · <strong>{fmtNum(summaryData.meta.unattributedLegs)}</strong> call
                {summaryData.meta.unattributedLegs === 1 ? ' is' : 's are'} not linked to a directory
                extension and {summaryData.meta.unattributedLegs === 1 ? 'is' : 'are'} excluded from this table.</>
              )}
            </div>
            {summaryData.rows.length === 0
              ? <div className={s.centeredMsg} style={{ minHeight: 160 }}>No extensions match these filters</div>
              : <SummaryMatrix data={summaryData} />}
          </div>
        )}

        {/* ══════════════ Tab 3: BY OFFICER ══════════════ */}
        {activeTab === 'officer' && !loading && officerData && (
          <div className={s.sectionCard}>
            <div className={s.sectionTitle}>By Officer / Extension</div>
            <div className={s.sectionSub}>
              Per-person call detail · staff only · {fmtNum(officerData.detail.length)} people
              {officerData.detail.length >= officerData.meta.capped ? ` (capped at ${officerData.meta.capped})` : ''} · click a header to sort
            </div>
            {officerData.detail.length === 0
              ? <div className={s.centeredMsg} style={{ minHeight: 160 }}>No calls match these filters</div>
              : (
                <SortableTable<OfficerRow>
                  rows={officerData.detail}
                  initialSort="total"
                  columns={[
                    { key: 'owner', label: 'Owner', render: r => <span style={{ fontWeight: 600 }}>{r.owner}</span> },
                    { key: 'extension', label: 'Extension' },
                    { key: 'personType', label: 'Type' },
                    { key: 'department', label: 'Department' },
                    { key: 'inbound', label: 'Inbound', num: true, render: r => fmtNum(r.inbound) },
                    { key: 'outbound', label: 'Outbound', num: true, render: r => fmtNum(r.outbound) },
                    { key: 'answered', label: 'Answered', num: true, render: r => fmtNum(r.answered) },
                    { key: 'abandoned', label: 'Abandoned', num: true, render: r => fmtNum(r.abandoned) },
                    { key: 'total', label: 'Total', num: true, render: r => <span style={{ fontWeight: 700 }}>{fmtNum(r.total)}</span> },
                    { key: 'avgDurationMin', label: 'Avg Dur', num: true, render: r => fmtMin(r.avgDurationMin) },
                  ]}
                />
              )}
          </div>
        )}

        {/* ══════════════ Tab 4: AUTO ATTENDANTS & QUEUES ══════════════ */}
        {activeTab === 'auto' && !loading && autoData && (
          <>
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 7, marginBottom: 16,
              fontSize: 12, color: '#3730A3', background: '#EEF2FF', border: '1px solid #C7D2FE',
              borderRadius: 6, padding: '8px 12px', lineHeight: 1.5,
            }}>
              <Info size={14} color="#4F46E5" style={{ marginTop: 2, flexShrink: 0 }} />
              <span>
                These are the shared numbers — main switchboards, auto-attendants and call queues — rather
                than individual staff. They handle the highest call volumes. Call pickup and transfer detail
                for these numbers is now captured from Teams call records and flows into the By Department
                summary; a dedicated queue-performance view (wait times, overflow) is planned.
              </span>
            </div>
            <div className={s.kpiRow} style={{ gridTemplateColumns: 'repeat(4, minmax(0,1fr))' }}>
              <div className={s.kpiCard}>
                <div className={s.kpiLabel}>Calls to Shared Numbers</div>
                <div className={s.kpiValue} style={{ color: INDIGO }}>{fmtNum(autoData.kpis.totalCalls)}</div>
                <div className={s.kpiSub}>switchboards, auto-attendants &amp; queues</div>
              </div>
              <div className={s.kpiCard}>
                <div className={s.kpiLabel}>Inbound</div>
                <div className={s.kpiValue} style={{ color: '#1A1A1A' }}>{fmtNum(autoData.kpis.inbound)}</div>
                <div className={s.kpiSub}>to these numbers</div>
              </div>
              <div className={s.kpiCard}>
                <div className={s.kpiLabel}>Answered</div>
                <div className={s.kpiValue} style={{ color: answeredColor(autoData.kpis.answeredPct) }}>
                  {autoData.kpis.answeredPct !== null ? `${autoData.kpis.answeredPct.toFixed(1)}%` : '—'}
                </div>
                <div className={s.kpiSub}>connected</div>
              </div>
              <div className={s.kpiCard}>
                <div className={s.kpiLabel}>Abandoned</div>
                <div className={s.kpiValue} style={{ color: (autoData.kpis.abandonedPct ?? 0) > 0 ? RED : '#1A1A1A' }}>
                  {autoData.kpis.abandonedPct !== null ? `${autoData.kpis.abandonedPct.toFixed(1)}%` : '—'}
                </div>
                <div className={s.kpiSub}>not connected</div>
              </div>
            </div>
            <div className={s.sectionCard}>
              <div className={s.sectionTitle}>Auto Attendants &amp; Queues</div>
              <div className={s.sectionSub}>
                Switchboards, auto-attendants and call queues · {fmtNum(autoData.detail.length)} shared numbers
                {autoData.detail.length >= autoData.meta.capped ? ` (capped at ${autoData.meta.capped})` : ''} · click a header to sort
              </div>
              {autoData.detail.length === 0
                ? <div className={s.centeredMsg} style={{ minHeight: 160 }}>No calls to shared numbers in this period</div>
                : (
                  <SortableTable<AutoRow>
                    rows={autoData.detail}
                    initialSort="total"
                    columns={[
                      { key: 'owner', label: 'Switchboard / Queue', render: r => <span style={{ fontWeight: 600 }}>{r.owner}</span> },
                      { key: 'extension', label: 'Extension' },
                      { key: 'inbound', label: 'Inbound', num: true, render: r => fmtNum(r.inbound) },
                      { key: 'outbound', label: 'Outbound', num: true, render: r => fmtNum(r.outbound) },
                      { key: 'answered', label: 'Answered', num: true, render: r => fmtNum(r.answered) },
                      { key: 'abandoned', label: 'Abandoned', num: true, render: r => fmtNum(r.abandoned) },
                      { key: 'total', label: 'Total', num: true, render: r => <span style={{ fontWeight: 700 }}>{fmtNum(r.total)}</span> },
                      { key: 'avgDurationMin', label: 'Avg Dur', num: true, render: r => fmtMin(r.avgDurationMin) },
                    ]}
                  />
                )}
            </div>
          </>
        )}

      </div>
    </div>
  );
}
